import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useState } from 'react';
import type {
  ChatMessagePreview,
  ChatPreviewLinkBehavior,
} from 'utils/chatLinks';
import { getChatPreviewSubtitle } from 'utils/chatLinks';

/** Preview shape for link cards; matches ChatMessagePreview from chatLinks. */
type LinkPreviewData = ChatMessagePreview;

interface LinkPreviewProps {
  preview: LinkPreviewData;
  behavior: ChatPreviewLinkBehavior | null;
  onOpenLink: (url: string) => void;
  isCurrentUser?: boolean;
}

const PreviewCard = styled(Box, {
  shouldForwardProp: (p) =>
    p !== 'isClickable' &&
    p !== 'isCurrentUser' &&
    p !== '$isClickable' &&
    p !== '$isCurrentUser',
})<{ $isClickable: boolean; $isCurrentUser: boolean }>(
  ({ theme, $isClickable, $isCurrentUser }) => ({
    borderRadius: theme.baseUnit * 2,
    border: `1px solid ${theme.colors.gray[300]}`,
    overflow: 'hidden',
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    backgroundColor: $isCurrentUser
      ? theme.colors.gray[200]
      : theme.colors.alias.mainBackground,
    cursor: $isClickable ? 'pointer' : 'default',
    '&:hover': $isClickable
      ? { backgroundColor: theme.colors.gray[100] }
      : undefined,
  }),
);

const PreviewImage = styled('img')(({ theme }) => ({
  width: '100%',
  height: 120,
  objectFit: 'cover',
  borderTopLeftRadius: theme.baseUnit * 2,
  borderTopRightRadius: theme.baseUnit * 2,
}));

const PreviewContent = styled(Box)(({ theme }) => ({
  padding: theme.baseUnit,
}));

const PreviewTitle = styled(Typography)(({ theme }) => ({
  ...theme.typography.fontFamily.sharp600,
  fontSize: 14,
  lineHeight: 1.35,
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  paddingBlock: 2,
  color: theme.colors.alias.textPrimary,
}));

const PreviewSubtitle = styled(Typography)(({ theme }) => ({
  ...theme.typography.fontFamily.reader400,
  fontSize: 12,
  lineHeight: 1.35,
  display: 'block',
  marginTop: theme.baseUnit / 4,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: theme.colors.gray[600],
}));

const OpenHint = styled(Typography)(({ theme }) => ({
  ...theme.typography.fontFamily.reader400,
  fontSize: 12,
  marginTop: theme.baseUnit / 4,
  display: 'flex',
  alignItems: 'center',
  gap: theme.baseUnit / 2,
  color: theme.colors.alias.primary,
}));

/**
 * Renders a single link preview as a clickable card (in-app or new tab).
 * Contract: preview.url must be non-empty; otherwise renders nothing.
 */
export function LinkPreview({
  preview,
  behavior,
  onOpenLink,
  isCurrentUser = false,
}: LinkPreviewProps) {
  const url = preview.url?.trim() ?? '';
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  const title = preview.title?.trim() ?? '';
  const imageUrl = preview.image?.url ?? preview.image?.preview ?? null;
  const isClickable = behavior !== null;
  const showImage = imageUrl && !imageLoadFailed;
  const subtitle = getChatPreviewSubtitle(preview, behavior);

  if (!url) {
    return null;
  }

  const cardContent = (
    <>
      {showImage && (
        <PreviewImage
          src={imageUrl ?? ''}
          alt=""
          onError={() => setImageLoadFailed(true)}
        />
      )}
      <PreviewContent>
        {title ? <PreviewTitle>{title}</PreviewTitle> : null}
        {subtitle ? <PreviewSubtitle>{subtitle}</PreviewSubtitle> : null}
        {isClickable && (
          <OpenHint>
            Open <span aria-hidden>→</span>
          </OpenHint>
        )}
      </PreviewContent>
    </>
  );

  return (
    <PreviewCard
      data-testid="link-preview"
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      $isClickable={isClickable}
      $isCurrentUser={isCurrentUser}
      onClick={
        isClickable
          ? () => {
              onOpenLink(url);
            }
          : undefined
      }
      onKeyDown={
        isClickable
          ? (e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpenLink(url);
              }
            }
          : undefined
      }
    >
      {cardContent}
    </PreviewCard>
  );
}
